import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { billingApi } from '../services/api';
import './Join.css';

// Stripe redirects here after Checkout. Access is granted by the webhook, which
// can land a few seconds later, so poll until the membership shows up.
const POLL_MS = 2000;
const GIVE_UP_MS = 30000;

const JoinSuccessPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [state, setState] = useState<'waiting' | 'active' | 'slow'>('waiting');
  const [planName, setPlanName] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const started = Date.now();
    const poll = async () => {
      try {
        const me = await billingApi.getMine();
        const paid = me.subscriptions.find((s) => s.source === 'stripe');
        if (paid) {
          if (cancelled) return;
          setPlanName(paid.plan.name);
          setState('active');
          queryClient.invalidateQueries({ queryKey: ['billing'] });
          return;
        }
      } catch {
        // keep polling
      }
      if (cancelled) return;
      if (Date.now() - started > GIVE_UP_MS) setState('slow');
      else setTimeout(poll, POLL_MS);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  return (
    <div className="join-page join-success">
      {state === 'active' ? (
        <>
          <h1>You’re in!</h1>
          <p className="join-lede">Your {planName} membership is active. Happy practicing.</p>
          <div className="join-success-actions">
            <Link to="/library" className="btn btn-primary">Go to the library</Link>
            <Link to="/profile" className="btn btn-secondary">View your membership</Link>
          </div>
        </>
      ) : state === 'slow' ? (
        <>
          <h1>Payment received</h1>
          <p className="join-lede">
            We’re still activating your membership. It usually takes a few seconds. Refresh this page in a minute.
            You won’t be charged twice.
          </p>
          <Link to="/library" className="btn btn-secondary">Go to the library</Link>
        </>
      ) : (
        <>
          <h1>Activating your membership…</h1>
          <p className="join-lede">Thanks for joining! This only takes a moment.</p>
        </>
      )}
    </div>
  );
};

export default JoinSuccessPage;
